import { CBCore, SocketClient } from "cbcore-ts"
import { nil, NO, UIColor, UICore, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CBDialogView } from "./Custom components/CBDialogView"
import { CBDialogViewShower } from "./Custom components/CBDialogViewShower"
import { EChartsView } from "./Custom components/EChartsView"
import { RHButton } from "./Custom components/RH/RHButton"
import { RHColor } from "./Custom components/RH/RHColor"
import { RHRStatisticsComparisonViewController } from "./RHRStatisticsComparisonViewController"
import { RHRStatisticsViewController } from "./RHRStatisticsViewController"


function withDelayedLoadingIndicator<T>(
    promise: Promise<T>,
    {
        message = "Loading...",
        delay = 200,
        parent = UICore.main.rootViewController.view
    } = {}
) {
    let dialogViewShower: CBDialogViewShower<CBDialogView<UIView>> | null | undefined = null
    const timeout = setTimeout(() => {
        dialogViewShower = CBDialogViewShower.showActionIndicatorDialog(
            message,
            nil,
            parent
        )
    }, delay)
    
    return promise.finally(() => {
        clearTimeout(timeout)
        dialogViewShower?.dialogView?.dismiss()
    })
}


export class MainViewController extends UIViewController {
    
    
    readonly titleLabel: UITextView = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header2
    ).configuredWithObject({
        text: "RHR avalik statistika",
        hoverText: "",
        backgroundColor: UIColor.transparentColor,
        textAlignment: UITextView.textAlignment.center,
        style: {
            fontSize: "24px",
            fontWeight: "600",
            color: "#212529", // Dark grey from example
            marginTop: "40px",
            marginBottom: "20px"
        }
    }).addedAsSubviewToView(this.view)
    
    explanationView: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        text: "Siin on võimalik võrrelda hankijaid " +
            "<a href=\"https://riigihanked.riik.ee/rhr-web/\" style=\"color: #0d6efd; text-decoration: underline;\">" +
            "Riigihangete registri" +
            "</a> " +
            "avaliku info põhjal koostatud statistika võtmes.",
        textAlignment: UITextView.textAlignment.center,
        isSingleLine: NO,
        style: {
            fontSize: "18px",
            color: "#6c757d", // Muted text color
            lineHeight: "1.6",
            maxWidth: "800px",
            margin: "0 auto" // Center block
        }
    })
    
    // Grouping buttons for layout convenience
    procurerButtonsContainer = new UIView().addedAsSubviewToView(this.view)
    offererButtonsContainer = new UIView().addedAsSubviewToView(this.view)
    
    procurerStatisticsButton: RHButton = new RHButton().addedAsSubviewToView(this.procurerButtonsContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Hankija statistika"
            },
            variant: "primary", // Blue
            controlEventTargetAccumulator: {
                PointerUpInside: (sender, event) => {
                    UIRoute.currentRoute.routeWithComponent(
                        RHRStatisticsViewController.routeComponentName,
                        { [RHRStatisticsViewController.ParameterIdentifierName.entityType]: "procurer" }
                    ).apply()
                }
            },
            hoverText: "Vaata ühe hankija statistikat erinevatel perioodidel",
            style: {
                width: "250px",
                height: "50px",
                fontSize: "18px"
            }
        })
    
    procurersComparisonButton: RHButton = new RHButton().addedAsSubviewToView(this.procurerButtonsContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Hankijate võrdlus"
            },
            variant: "primary", // Blue
            controlEventTargetAccumulator: {
                PointerUpInside: (sender, event) => {
                    UIRoute.currentRoute.routeWithComponent(
                        RHRStatisticsComparisonViewController.routeComponentName,
                        {
                            [RHRStatisticsComparisonViewController.ParameterIdentifierName.entityType]: "procurer"
                        }
                    ).apply()
                }
            },
            hoverText: "Võrdle erinevate hankijate statistikat",
            style: {
                width: "250px",
                height: "50px",
                fontSize: "18px"
            }
        })
    
    offererStatisticsButton: RHButton = new RHButton().addedAsSubviewToView(this.offererButtonsContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Pakkuja statistika"
            },
            variant: "success", // Green
            controlEventTargetAccumulator: {
                PointerUpInside: (sender, event) => {
                    UIRoute.currentRoute.routeWithComponent(
                        RHRStatisticsViewController.routeComponentName,
                        { [RHRStatisticsViewController.ParameterIdentifierName.entityType]: "offerer" }
                    ).apply()
                }
            },
            hoverText: "Vaata ühe pakkuja statistikat erinevatel perioodidel",
            style: {
                width: "250px",
                height: "50px",
                fontSize: "18px"
            }
        })
    
    offerersComparisonButton: RHButton = new RHButton().addedAsSubviewToView(this.offererButtonsContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Pakkujate võrdlus"
            },
            variant: "success", // Green
            controlEventTargetAccumulator: {
                PointerUpInside: (sender, event) => {
                    UIRoute.currentRoute.routeWithComponent(
                        RHRStatisticsComparisonViewController.routeComponentName,
                        {
                            [RHRStatisticsComparisonViewController.ParameterIdentifierName.entityType]: "offerer"
                        }
                    ).apply()
                }
            },
            hoverText: "Võrdle erinevate pakkujate statistikat",
            style: {
                width: "250px",
                height: "50px",
                fontSize: "18px"
            }
        })
    
    
    frontDashboardView = new UIView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            // Removed default setBorder for a cleaner look, or use a subtle shadow if needed
            style: {
                // marginTop: "40px"
            },
            layoutSubviews: () => {
                const bounds = this.frontDashboardView.bounds
                this.topStatisticsChart.frame = bounds
            }
        })
    
    
    // Toggle Buttons for Chart
    chartTogglesContainer = new UIView().addedAsSubviewToView(this.view)
    
    topProcurersByCountButton = new RHButton().addedAsSubviewToView(this.chartTogglesContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Hankijad (Arv)"
            },
            variant: "warning", // Default selected state for this specific logic
            controlEventTargetAccumulator: {
                // @ts-ignore
                EnterDown: {
                    PointerUpInside: (sender, event) => {
                        this.updateChartButtonsSelection(this.topProcurersByCountButton)
                        this.topStatisticsChart.dataAndConfiguration = this
                            ._mostProcurerContractsChartDataAndConfiguration
                    }
                }
            }
        })
    
    topProcurersByMonetarySumButton = new RHButton()
        .addedAsSubviewToView(this.chartTogglesContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Hankijad (Maksumus)"
            },
            variant: "light",
            controlEventTargetAccumulator: {
                // @ts-ignore
                EnterDown: {
                    PointerUpInside: (sender, event) => {
                        this.updateChartButtonsSelection(this.topProcurersByMonetarySumButton)
                        this.topStatisticsChart.dataAndConfiguration = this
                            ._mostProcurerContractsMoneyChartDataAndConfiguration
                    }
                }
            }
        })
    
    topOfferersByCountButton = new RHButton()
        .addedAsSubviewToView(this.chartTogglesContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Partnerid (Arv)"
            },
            variant: "light",
            controlEventTargetAccumulator: {
                // @ts-ignore
                EnterDown: {
                    PointerUpInside: (sender, event) => {
                        this.updateChartButtonsSelection(this.topOfferersByCountButton)
                        this.topStatisticsChart.dataAndConfiguration = this
                            ._mostOffererContractsChartDataAndConfiguration
                    }
                }
            }
        })
    
    topOfferersByMonetarySumButton = new RHButton().addedAsSubviewToView(this.chartTogglesContainer)
        .configuredWithObject({
            titleLabel: {
                text: "Partnerid (Maksumus)"
            },
            variant: "light",
            controlEventTargetAccumulator: {
                // @ts-ignore
                EnterDown: {
                    PointerUpInside: (sender, event) => {
                        this.updateChartButtonsSelection(this.topOfferersByMonetarySumButton)
                        this.topStatisticsChart.dataAndConfiguration = this
                            ._mostOffererContractsMoneyChartDataAndConfiguration
                    }
                }
            }
        })
    
    _mostProcurerContractsChartDataAndConfiguration: typeof this.topStatisticsChart.dataAndConfiguration = {}
    _mostProcurerContractsMoneyChartDataAndConfiguration: typeof this.topStatisticsChart.dataAndConfiguration = {}
    _mostOffererContractsChartDataAndConfiguration: typeof this.topStatisticsChart.dataAndConfiguration = {}
    _mostOffererContractsMoneyChartDataAndConfiguration: typeof this.topStatisticsChart.dataAndConfiguration = {}
    
    topStatisticsChart = new EChartsView().addedAsSubviewToView(this.frontDashboardView)
    
    
    bottomView = new UIView().addedAsSubviewToView(this.view)
    
    
    constructor(view: UIView) {
        
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor,
            hoverText: ""
        })
        
        this.initDashboard()
        
    }
    
    updateChartButtonsSelection(selectedButton: RHButton) {
        const buttons = [
            this.topProcurersByCountButton,
            this.topProcurersByMonetarySumButton,
            this.topOfferersByCountButton,
            this.topOfferersByMonetarySumButton
        ]
        
        buttons.forEach(btn => {
            if (btn == selectedButton) {
                btn.selected = YES
                btn.variant = "warning" // Highlighted
            }
            else {
                btn.selected = NO
                btn.variant = "light" // Neutral
            }
        })
    }
    
    
    async initDashboard() {
        
        
        const periodStartDate = new Date()
        periodStartDate.setUTCHours(0)
        periodStartDate.setUTCMinutes(0)
        periodStartDate.setUTCSeconds(0)
        periodStartDate.setUTCMilliseconds(0)
        periodStartDate.setUTCFullYear(periodStartDate.getUTCFullYear() - 1)
        
        const [
            topProcurersStatistics,
            topOfferersStatistics
        ] = await withDelayedLoadingIndicator(
            CBCore.sharedInstance.socketClient.sendMessagesAsGroup(async () =>
                [
                    (await SocketClient.TopProcurerStatistics({})).result,
                    (await SocketClient.TopOffererStatistics({})).result
                ]
            ),
            { message: "Loading statistics", delay: 200, parent: this.frontDashboardView }
        )
        
        const topProcurersStatisticsSortedForMostContracts = topProcurersStatistics.sort(
            (a, b) => b.numberOfContracts - a.numberOfContracts || b.contractMonetarySum - a.contractMonetarySum
        ).slice(0, 300)
        
        const topProcurersStatisticsSortedForMostMoney = topProcurersStatistics.sort(
            (a, b) => b.contractMonetarySum - a.contractMonetarySum || b.numberOfContracts - a.numberOfContracts
        ).slice(0, 300)
        
        
        const topOfferersStatisticsSortedForMostContracts = topOfferersStatistics.sort(
            (a, b) => b.numberOfContracts - a.numberOfContracts || b.contractMonetarySum - a.contractMonetarySum
        ).slice(0, 300)
        
        const topOfferersStatisticsSortedForMostMoney = topOfferersStatistics.sort(
            (a, b) => b.contractMonetarySum - a.contractMonetarySum || b.numberOfContracts - a.numberOfContracts
        ).slice(0, 300)
        
        
        const cbValueFormatter = (
            value: any,
            showTooltipEvent: {
                type: "showtip",
                dataIndexInside: number,
                dataIndex: number,
                seriesIndex: number,
                from: string
            }
        ) => {
            
            // Assuming seriesIndex 1 is typically the monetary value in your config
            if (showTooltipEvent?.seriesIndex == 1) {
                // Ensure currencyFormatter is available in context or import it
                // Using a fallback implementation if strict import unavailable in snippet
                return (value as number).toFixed(2) + " €"
            }
            
            return value
            
        }
        
        function normalizedNames(names: string[]) {
            function capitalize(str: string) {
                if (!str) {
                    return ""
                }
                return str.charAt(0).toUpperCase() + str.slice(1)
            }
            
            const wordsToRemove = ["oü", "as", "aktsiaselts", "osaühing", "sihtasutus", "sa"]
            const result = names.map(
                name => capitalize(
                    name.split(" ")
                        .filter(
                            word => !wordsToRemove.contains(word.toLowerCase())
                        ).join(" ")
                )
            )
            
            return result
        }
        
        
        // Configuration objects (kept largely similar but ensuring proper context)
        this._mostProcurerContractsChartDataAndConfiguration = {
            title: {
                text: "Eesti hankijate TOP 30 (Lepingute arv)",
                subtext: "Alates " + periodStartDate.dateString,
                left: "center",
                top: 10
            },
            grid: { left: 80, right: 80, containLabel: true },
            tooltip: { trigger: "item", cb_valueFormatter: cbValueFormatter },
            xAxis: {
                type: "category",
                data: normalizedNames(
                    topProcurersStatisticsSortedForMostContracts.everyElement.entityName.UI_elementValues ?? []),
                axisLabel: { interval: 0, rotate: -45, fontSize: 14, margin: 15 }
            },
            yAxis: [
                {
                    type: "value",
                    name: "Lepingute arv",
                    splitLine: { show: true },
                    nameLocation: "middle",
                    nameGap: 50
                },
                {
                    type: "value",
                    name: "Maksumus (EUR)",
                    position: "right",
                    splitLine: { show: false },
                    nameLocation: "middle",
                    nameGap: 80,
                    nameRotate: -90
                }
            ],
            dataZoom: [
                { type: "slider", start: 0, end: 10, zoomLock: true },
                { type: "inside", zoomOnMouseWheel: false }
            ],
            series: [
                {
                    name: "Lepingute arv",
                    type: "bar",
                    data: topProcurersStatisticsSortedForMostContracts.everyElement.numberOfContracts.UI_elementValues,
                    yAxisIndex: 0,
                    itemStyle: { color: "#0d6efd" }
                },
                {
                    name: "Maksumus",
                    type: "bar",
                    data: topProcurersStatisticsSortedForMostContracts.everyElement.contractMonetarySum.UI_elementValues,
                    yAxisIndex: 1,
                    itemStyle: { color: "#fd7e14" }
                }
            ]
        }
        
        this.topStatisticsChart.dataAndConfiguration = this._mostProcurerContractsChartDataAndConfiguration
        this.topProcurersByCountButton.selected = YES
        
        this.view.setNeedsLayoutUpToRootView()
        
        this._mostProcurerContractsMoneyChartDataAndConfiguration = {
            title: {
                text: "Eesti hankijate TOP 30 (Maksumus)",
                subtext: "Alates " + periodStartDate.dateString,
                left: "center",
                top: 10
            },
            grid: { left: 80, right: 80, containLabel: true },
            tooltip: { trigger: "item", cb_valueFormatter: cbValueFormatter },
            xAxis: {
                type: "category",
                data: normalizedNames(
                    topProcurersStatisticsSortedForMostMoney.everyElement.entityName.UI_elementValues ?? []),
                axisLabel: { interval: 0, rotate: -45, fontSize: 14, margin: 15 }
            },
            yAxis: [
                {
                    type: "value",
                    name: "Lepingute arv",
                    splitLine: { show: true },
                    nameLocation: "middle",
                    nameGap: 50
                },
                {
                    type: "value",
                    name: "Maksumus (EUR)",
                    position: "right",
                    splitLine: { show: false },
                    nameLocation: "middle",
                    nameGap: 80,
                    nameRotate: -90
                }
            ],
            dataZoom: [{ type: "slider", start: 0, end: 10 }, { type: "inside", zoomOnMouseWheel: false }],
            series: [
                {
                    name: "Lepingute arv",
                    type: "bar",
                    data: topProcurersStatisticsSortedForMostMoney.everyElement.numberOfContracts.UI_elementValues,
                    yAxisIndex: 0,
                    itemStyle: { color: "#0d6efd" }
                },
                {
                    name: "Maksumus",
                    type: "bar",
                    data: topProcurersStatisticsSortedForMostMoney.everyElement.contractMonetarySum.UI_elementValues,
                    yAxisIndex: 1,
                    itemStyle: { color: "#fd7e14" }
                }
            ]
        }
        
        this._mostOffererContractsChartDataAndConfiguration = {
            title: {
                text: "Lepingupartnerite TOP 30 (Lepingute arv)",
                subtext: "Alates " + periodStartDate.dateString,
                left: "center",
                top: 10
            },
            grid: { left: 80, right: 80, containLabel: true },
            tooltip: { trigger: "item", cb_valueFormatter: cbValueFormatter },
            xAxis: {
                type: "category",
                data: normalizedNames(
                    topOfferersStatisticsSortedForMostContracts.everyElement.entityName.UI_elementValues ?? []),
                axisLabel: { interval: 0, rotate: -45, fontSize: 14, margin: 15 }
            },
            yAxis: [
                {
                    type: "value",
                    name: "Lepingute arv",
                    splitLine: { show: true },
                    nameLocation: "middle",
                    nameGap: 50
                },
                {
                    type: "value",
                    name: "Maksumus (EUR)",
                    position: "right",
                    splitLine: { show: false },
                    nameLocation: "middle",
                    nameGap: 80,
                    nameRotate: -90
                }
            ],
            dataZoom: [{ type: "slider", start: 0, end: 10 }, { type: "inside", zoomOnMouseWheel: false }],
            series: [
                {
                    name: "Lepingute arv",
                    type: "bar",
                    data: topOfferersStatisticsSortedForMostContracts.everyElement.numberOfContracts.UI_elementValues,
                    yAxisIndex: 0,
                    itemStyle: { color: "#0d6efd" }
                },
                {
                    name: "Maksumus",
                    type: "bar",
                    data: topOfferersStatisticsSortedForMostContracts.everyElement.contractMonetarySum.UI_elementValues,
                    yAxisIndex: 1,
                    itemStyle: { color: "#fd7e14" }
                }
            ]
        }
        
        this._mostOffererContractsMoneyChartDataAndConfiguration = {
            title: {
                text: "Lepingupartnerite TOP 30 (Maksumus)",
                subtext: "Alates " + periodStartDate.dateString,
                left: "center",
                top: 10
            },
            grid: { left: 80, right: 80, containLabel: true },
            tooltip: { trigger: "item", cb_valueFormatter: cbValueFormatter },
            xAxis: {
                type: "category",
                data: normalizedNames(
                    topOfferersStatisticsSortedForMostMoney.everyElement.entityName.UI_elementValues ?? []),
                axisLabel: { interval: 0, rotate: -45, fontSize: 14, margin: 15 }
            },
            yAxis: [
                {
                    type: "value",
                    name: "Lepingute arv",
                    splitLine: { show: true },
                    nameLocation: "middle",
                    nameGap: 50
                },
                {
                    type: "value",
                    name: "Maksumus (EUR)",
                    position: "right",
                    splitLine: { show: false },
                    nameLocation: "middle",
                    nameGap: 80,
                    nameRotate: -90
                }
            ],
            dataZoom: [{ type: "slider", start: 0, end: 10 }, { type: "inside", zoomOnMouseWheel: false }],
            series: [
                {
                    name: "Lepingute arv",
                    type: "bar",
                    data: topOfferersStatisticsSortedForMostMoney.everyElement.numberOfContracts.UI_elementValues,
                    yAxisIndex: 0,
                    itemStyle: { color: "#0d6efd" }
                },
                {
                    name: "Maksumus",
                    type: "bar",
                    data: topOfferersStatisticsSortedForMostMoney.everyElement.contractMonetarySum.UI_elementValues,
                    yAxisIndex: 1,
                    itemStyle: { color: "#fd7e14" }
                }
            ]
        }
        
    }
    
    static override readonly routeComponentName = "home"
    static override readonly ParameterIdentifierName = {}
    
    override async handleRoute(route: UIRoute) {
        super.handleRoute(route)
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        
        // Define variables for layout dimensions
        const largeButtonHeight = padding * 2.5
        const smallButtonHeight = padding * 2
        const gap = padding * 0.8
        const contentWidth = 800
        const buttonWidth = 250
        const buttonRowWidth = (buttonWidth * 2) + gap
        const chartHeight = 800
        
        const bounds = this.view.bounds.rectangleWithInset(padding)
        
        // 1. Title
        this.titleLabel.frame = bounds.rectangleWithHeight(padding * 2).rectangleByAddingY(padding)
        
        // Start the Fluent Layout Chain
        this.titleLabel.frame
            
            // 2. Explanation
            .rectangleForNextRow(
                padding * 2,
                [this.explanationView.intrinsicContentHeight(contentWidth), padding * 3].max()
            )
            .rectangleWithWidth(contentWidth, 0.5) // Center
            .distributeViewsEquallyAlongWidth([this.explanationView], 0)
            
            // 3. Procurer Buttons (Row 1)
            .rectangleForNextRow(padding * 2, largeButtonHeight)
            .rectangleWithWidth(buttonRowWidth, 0.5) // Center buttons
            .distributeViewsEquallyAlongWidth([this.procurerStatisticsButton, this.procurersComparisonButton], gap)
            
            // 4. Offerer Buttons (Row 2)
            .rectangleForNextRow(gap, largeButtonHeight)
            // Width is already set by the previous link in the chain (buttonRowWidth)
            .distributeViewsEquallyAlongWidth([this.offererStatisticsButton, this.offerersComparisonButton], gap)
            
            // 5. Chart Toggles
            .rectangleForNextRow(padding * 3, smallButtonHeight)
            .rectangleWithWidth([bounds.width, 900].min(), 0.5) // Wider container for 4 buttons
            .distributeViewsEquallyAlongWidth([
                this.topProcurersByCountButton,
                this.topProcurersByMonetarySumButton,
                this.topOfferersByCountButton,
                this.topOfferersByMonetarySumButton
            ], gap)
            
            // 6. Chart View
            .rectangleForNextRow(padding, chartHeight)
            .rectangleWithWidth(bounds.width, 0.5) // Reset to full width
            .distributeViewsEquallyAlongWidth([this.frontDashboardView], 0)
            
            // 7. Bottom View
            .rectangleForNextRow(padding, 50)
            .distributeViewsEquallyAlongWidth([this.bottomView], 0)
        
    }
    
    
}
